import React, { createContext, useContext, useState, useEffect } from 'react';
import { siteContentApi, productsApi } from '../services/api';

const SiteContext = createContext();

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

// Empty placeholder data - will be replaced by API data
const emptyData = {
  config: { name: '', tagline: '', socialMedia: {} },
  hero: { title: '', description: '', buttonText: '', buttonLink: '', image: '' },
  sections: [],
  testimonials: { title: '', reviews: [], image: '' },
  commitment: { title: 'Our Commitment', content: '' },
  featuredProducts: [],
  products: [],
};

export const SiteProvider = ({ children }) => {
  const [siteData, setSiteData] = useState(emptyData);
  const [loading, setLoading] = useState(true);

  // Fetch data from API on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, productsRes] = await Promise.all([
          siteContentApi.get(),
          productsApi.getAll()
        ]);
        
        setSiteData({
          config: contentRes.data.config || emptyData.config,
          hero: contentRes.data.hero || emptyData.hero,
          sections: contentRes.data.sections || emptyData.sections,
          testimonials: contentRes.data.testimonials || emptyData.testimonials,
          commitment: contentRes.data.commitment || emptyData.commitment,
          featuredProducts: contentRes.data.featuredProducts || emptyData.featuredProducts,
          products: productsRes.data || emptyData.products,
        });
      } catch (error) {
        console.error('Failed to fetch site data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const updateHero = async (newHero) => {
    try {
      await siteContentApi.update({ hero: newHero });
      setSiteData((prev) => ({ ...prev, hero: newHero }));
    } catch (error) {
      console.error('Failed to update hero:', error);
      throw error;
    }
  };

  const updateSections = async (newSections) => {
    try {
      await siteContentApi.update({ sections: newSections });
      setSiteData((prev) => ({ ...prev, sections: newSections }));
    } catch (error) {
      console.error('Failed to update sections:', error);
      throw error;
    }
  };

  const updateTestimonials = async (newTestimonials) => {
    try {
      await siteContentApi.update({ testimonials: newTestimonials });
      setSiteData((prev) => ({ ...prev, testimonials: newTestimonials }));
    } catch (error) {
      console.error('Failed to update testimonials:', error);
      throw error;
    }
  };

  const updateProducts = async (newProducts) => {
    try {
      // Update each product individually
      for (const product of newProducts) {
        await productsApi.update(product.id, product);
      }
      setSiteData((prev) => ({ ...prev, products: newProducts }));
    } catch (error) {
      console.error('Failed to update products:', error);
      throw error;
    }
  };

  const updateConfig = async (newConfig) => {
    try {
      await siteContentApi.update({ config: newConfig });
      setSiteData((prev) => ({ ...prev, config: newConfig }));
    } catch (error) {
      console.error('Failed to update config:', error);
      throw error;
    }
  };

  const updateCommitment = async (newCommitment) => {
    try {
      await siteContentApi.update({ commitment: newCommitment });
      setSiteData((prev) => ({ ...prev, commitment: newCommitment }));
    } catch (error) {
      console.error('Failed to update commitment:', error);
      throw error;
    }
  };

  const updateFeaturedProducts = async (newFeaturedProducts) => {
    try {
      await siteContentApi.update({ featuredProducts: newFeaturedProducts });
      setSiteData((prev) => ({ ...prev, featuredProducts: newFeaturedProducts }));
    } catch (error) {
      console.error('Failed to update featured products:', error);
      throw error;
    }
  };

  const resetToDefaults = async () => {
    // Reset to defaults is now handled by the admin panel
    // This function can refetch the current data from the server
    await refetchData();
  };

  const refetchData = async () => {
    setLoading(true);
    try {
      const [contentRes, productsRes] = await Promise.all([
        siteContentApi.get(),
        productsApi.getAll()
      ]);
      
      setSiteData({
        config: contentRes.data.config || emptyData.config,
        hero: contentRes.data.hero || emptyData.hero,
        sections: contentRes.data.sections || emptyData.sections,
        testimonials: contentRes.data.testimonials || emptyData.testimonials,
        commitment: contentRes.data.commitment || { title: 'Our Commitment', content: '' },
        featuredProducts: contentRes.data.featuredProducts || emptyData.featuredProducts,
        products: productsRes.data || emptyData.products,
      });
    } catch (error) {
      console.error('Failed to refetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteContext.Provider
      value={{
        siteData,
        loading,
        updateHero,
        updateSections,
        updateTestimonials,
        updateCommitment,
        updateFeaturedProducts,
        updateProducts,
        updateConfig,
        resetToDefaults,
        refetchData,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};
